<template>
    <div class="kpi-hero">
        <div class="kpi-total">
            <div class="kpi-label">
                {{ totalLabel }}
                <span v-if="isPredicted" class="ebc-badge is-warn">예측 포함</span>
            </div>
            <div class="kpi-total__value ebc-num">{{ num(displayFee) }}<small>원</small></div>
            <span v-if="totalDelta" class="kpi-delta ebc-num" :class="totalDelta.cls">
                <i :class="totalDelta.icon"></i>{{ totalDelta.text }}
            </span>
        </div>

        <div class="kpi-compose">
            <div class="kpi-label">요금 구성</div>
            <template v-if="composition.length">
                <div class="compose-bar">
                    <span
                        v-for="item in composition"
                        :key="item.key"
                        :style="{ flexGrow: item.amount, background: item.color }"
                        :title="`${item.name} ${pct(item.share)}`"
                    ></span>
                </div>
                <ul class="compose-list ebc-num">
                    <li v-for="item in composition" :key="item.key">
                        <i :style="{ background: item.color }"></i>
                        <span class="compose-name">{{ item.name }}</span>
                        <span class="compose-share">{{ pct(item.share, 0) }}</span>
                        <b>{{ num(item.amount) }}원</b>
                    </li>
                </ul>
            </template>
            <div v-else class="compose-empty">청구 내역이 없습니다</div>
        </div>

        <dl class="kpi-info" :class="`is-${infoItems.length >= 7 ? 'wide' : 'narrow'}`">
            <div
                v-for="item in infoItems"
                :key="item.key"
                :class="{ 'is-span': item.span && infoItems.length > 4 }"
                :title="item.title || null"
            >
                <dt>
                    {{ item.label }}
                    <i
                        v-if="item.info"
                        v-b-tooltip.hover="{
                            title: item.info,
                            html: true,
                            placement: 'bottom',
                            customClass: 'ebc-tooltip',
                        }"
                        class="fas fa-info-circle kpi-info__icon"
                    ></i>
                </dt>
                <dd class="ebc-num">
                    <span class="kpi-info__value" :class="{ 'is-text': item.text }">{{ item.value }}</span
                    ><small v-if="item.unit">{{ item.unit }}</small>
                    <span v-if="item.tag" class="kpi-info__tag">{{ item.tag }}</span>
                </dd>
                <div v-if="item.sub" class="kpi-info__sub" :class="item.subCls || 'is-muted'">
                    <i v-if="item.subIcon" :class="item.subIcon"></i>{{ item.sub }}
                </div>
            </div>
        </dl>
    </div>
</template>

<script>
import { isNum, num, pct, usageFeeLabel } from "./analysisFormat.js";
import { navyScale, grayScale } from "../../utils/chartTheme.js";

const FEE_ANIMATION_MS = 700;
const BASE_FEE_CALC_TYPES = {
    Fixed: "정액",
    Contract: "계약전력",
    Demand: "최대수요",
    Progressive: "누진제",
    PeakLinked: "피크연동",
    None: "없음",
};
const COMPOSE_COLORS = [navyScale[4], navyScale[3], navyScale[2], grayScale[2]];

function deltaOf(percent, label, { goodWhenDown = true } = {}) {
    if (!isNum(percent)) return null;
    const n = Number(percent);
    if (n === 0) return { text: `0.0% ${label}`, icon: "fas fa-minus", cls: "is-flat" };
    const up = n > 0;
    const good = goodWhenDown ? !up : up;
    return {
        text: `${pct(Math.abs(n))} ${label}`,
        icon: up ? "fas fa-caret-up" : "fas fa-caret-down",
        cls: good ? "is-good" : "is-bad",
    };
}

function ymText(regYm) {
    const s = String(regYm || "");
    return s.length === 6 ? `${s.substring(2, 4)}년 ${parseInt(s.substring(4, 6), 10)}월` : s;
}

export default {
    name: "KpiStrip",
    props: {
        analysis: {
            type: Object,
            required: true,
        },
        analysisType: {
            type: String,
            default: "monthly",
        },
        period: {
            type: String,
            default: "",
        },
    },
    computed: {
        current() {
            const ba = this.analysis.billAnalysis || {};
            return (this.analysisType === "yearly" ? ba.currentYear : ba.currentMonth) || {};
        },
        comparison() {
            return this.analysis.billAnalysis?.comparison || {};
        },
        previousDiff() {
            return this.analysisType === "yearly"
                ? { diff: this.comparison.vsPreviousYear, label: "전년 대비" }
                : { diff: this.comparison.vsPreviousMonth, label: "전월 대비" };
        },
        contract() {
            return this.analysis.contractInfo || {};
        },
        isPredicted() {
            return this.current.status === "predict" || !!this.analysis.breakdown?.predicted;
        },
        unit() {
            return this.current.rawUnit || this.contract.rawUnit || "kWh";
        },
        totalLabel() {
            const p = this.period;
            if (p.length === 6) return `${p.substring(0, 4)}년 ${parseInt(p.substring(4, 6), 10)}월 요금`;
            if (p.length === 4) return `${p}년 요금`;
            return this.analysisType === "yearly" ? "연간 요금" : "월 요금";
        },
        totalFee() {
            if (isNum(this.current.totalFee) && this.current.totalFee > 0) return this.current.totalFee;
            return this.current.predict?.totalFee ?? this.current.totalFee;
        },
        totalDelta() {
            const { diff, label } = this.previousDiff;
            return deltaOf(diff?.costPercent, label);
        },
        rawUsage() {
            if (isNum(this.current.rawUsage) && this.current.rawUsage > 0) return this.current.rawUsage;
            return this.current.predict?.rawUsage ?? this.current.rawUsage;
        },
        composition() {
            const b = this.analysis.breakdown;
            if (!b || !(b.totalFee > 0)) return [];
            return [
                { key: "base", name: "기본요금", amount: b.baseFee },
                { key: "usage", name: usageFeeLabel(this.contract.energySourceCode), amount: b.usageFee },
                { key: "etc", name: "기타요금", amount: b.etcFee },
                { key: "vat", name: "부가세", amount: b.vatAmount },
            ]
                .filter((item) => item.amount > 0)
                .map((item, i) => ({
                    ...item,
                    color: COMPOSE_COLORS[i % COMPOSE_COLORS.length],
                    share: (item.amount / b.totalFee) * 100,
                }));
        },
        isElec() {
            return this.contract.energySourceCode === "Elec";
        },
        peakHold() {
            return this.analysis.capacity?.applicable ? this.analysis.capacity.peakHold || null : null;
        },
        infoItems() {
            const { diff, label } = this.previousDiff;
            const withDelta = (percent) => {
                const d = deltaOf(percent, label);
                return d ? { sub: d.text, subCls: d.cls, subIcon: d.icon } : {};
            };
            const items = [
                {
                    key: "usage",
                    label: "사용량",
                    value: num(this.rawUsage),
                    unit: this.unit,
                    ...withDelta(diff?.usagePercent),
                },
                {
                    key: "unitPrice",
                    label: "평균 단가",
                    value: num(this.current.unitPrice, 1),
                    unit: `원/${this.unit}`,
                    ...withDelta(diff?.unitPricePercent),
                },
            ];
            if (this.isElec) {
                const cap = this.contract.contractCapacity;
                const peak = this.current.maxDemand;
                const util = isNum(cap) && cap > 0 && isNum(peak) ? (peak / cap) * 100 : null;
                items.push(
                    {
                        key: "peak",
                        label: `${this.periodName} 최대수요`,
                        value: num(peak),
                        unit: "kW",
                        tag: this.isPredicted ? "진행 중" : "",
                        sub: isNum(util) ? `${this.periodName} 이용률 ${pct(util)}` : "",
                    },
                    { key: "capacity", label: "계약전력", value: num(cap), unit: "kW", sub: "" },
                );
            }
            if (this.peakHold) items.push(this.peakHoldItem);
            items.push(
                {
                    key: "cost",
                    label: "요금제",
                    value: this.contract.costName || "-",
                    unit: "",
                    text: true,
                },
                {
                    key: "baseFeeCalcType",
                    label: "기본요금 방식",
                    value: BASE_FEE_CALC_TYPES[this.contract.baseFeeCalcType] || this.contract.baseFeeCalcType || "-",
                    unit: "",
                    text: true,
                },
                {
                    key: "reading",
                    label: "검침일",
                    value: this.contract.readingDay ? `매월 ${this.contract.readingDay}일` : "-",
                    unit: "",
                    text: true,
                },
            );
            return items;
        },
        periodName() {
            const p = this.period;
            if (this.analysisType === "yearly") return "연";
            return p.length === 6 ? `${parseInt(p.substring(4, 6), 10)}월` : "월";
        },
        peakHoldItem() {
            const p = this.peakHold;
            const billing = Math.max(p.floor, p.value);
            const unitPrice = this.analysis.capacity?.unitPrice;
            const until =
                p.remainingMonths > 0 ? `${ymText(p.validUntilYm)}까지 (${p.remainingMonths}개월)` : "이번 달까지";
            const after =
                p.nextValue > 0 ? `이후 피크 ${num(p.nextValue)}kW (${ymText(p.nextSourceYm)})` : "이후 반영할 피크 없음";
            const lines = [
                "기본요금 = 단가 × max(계약전력 30%, 적용피크)",
                isNum(unitPrice)
                    ? `<b>${num(unitPrice)}원 × max(${num(p.floor)}, ${num(p.value)}) = ${num(Math.floor(unitPrice * billing))}원</b>`
                    : "",
                `적용피크: ${ymText(p.sourceYm)} 최대수요 ${num(p.value)}kW · ${until}`,
                after,
            ];
            return {
                key: "applyPeak",
                label: "요금적용전력",
                value: num(billing),
                unit: "kW",
                sub: p.floorApplied
                    ? `하한 기준 (피크 ${num(p.value)}kW < 하한 ${num(p.floor)}kW)`
                    : `${ymText(p.sourceYm)} 피크 기준 · ${ymText(p.validUntilYm)}까지`,
                info: lines
                    .filter(Boolean)
                    .map((line) => `<div>${line}</div>`)
                    .join(""),
            };
        },
    },
    data() {
        return {
            displayFee: 0,
        };
    },
    watch: {
        totalFee: {
            immediate: true,
            handler(value) {
                this.animateFee(value);
            },
        },
    },
    beforeDestroy() {
        cancelAnimationFrame(this.feeFrame);
    },
    methods: {
        num,
        pct,
        animateFee(target) {
            cancelAnimationFrame(this.feeFrame);
            if (!isNum(target)) {
                this.displayFee = target;
                return;
            }
            const from = isNum(this.displayFee) ? Number(this.displayFee) : 0;
            const to = Number(target);
            const start = performance.now();
            const step = (now) => {
                const t = Math.min(1, (now - start) / FEE_ANIMATION_MS);
                const eased = 1 - Math.pow(1 - t, 3);
                this.displayFee = t < 1 ? Math.round(from + (to - from) * eased) : to;
                if (t < 1) this.feeFrame = requestAnimationFrame(step);
            };
            this.feeFrame = requestAnimationFrame(step);
        },
    },
};
</script>

<style scoped>
.kpi-hero {
    --kpi-text: #ffffff;
    --kpi-text-sub: var(--ebc-gray-4);
    --kpi-text-muted: var(--ebc-gray-3);
    --kpi-line: rgba(207, 226, 247, 0.16);
    --kpi-chip: rgba(207, 226, 247, 0.12);
    --kpi-good: #8fcfb3;
    --kpi-bad: #e3a5a5;
    --kpi-warn: #fdba74;
    display: grid;
    grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr) minmax(0, 2fr);
    background: linear-gradient(315deg, var(--ebc-navy-1) 0%, #1a294b 60%, #243557 100%);
    border: 1px solid var(--ebc-navy-1);
    border-radius: var(--ebc-radius-md);
    box-shadow: var(--ebc-shadow-panel, var(--ebc-shadow-card));
    color: var(--kpi-text);
    flex-shrink: 0;
}

.kpi-hero > * {
    min-width: 0;
    padding: 10px 18px;
}

.kpi-hero > * + * {
    border-left: 1px solid var(--kpi-line);
}

.kpi-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--kpi-text-sub);
}

.kpi-label .ebc-badge {
    background: rgba(253, 186, 116, 0.16);
    color: var(--kpi-warn);
}

.kpi-total {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
}

.kpi-total__value {
    font-size: 34px;
    font-weight: 800;
    line-height: 1.1;
    color: var(--kpi-text);
    letter-spacing: 0.01em;
    white-space: nowrap;
}

.kpi-total__value small {
    margin-left: 3px;
    font-size: 15px;
    font-weight: 600;
    color: var(--kpi-text-sub);
}

.kpi-delta {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 700;
    border-radius: var(--ebc-radius-pill);
    background: var(--kpi-chip);
}

.is-good {
    color: var(--kpi-good);
}

.is-bad {
    color: var(--kpi-bad);
}

.is-flat,
.is-muted {
    color: var(--kpi-text-muted);
}

.is-warn {
    color: var(--kpi-warn);
}

.kpi-compose {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.compose-bar {
    display: flex;
    gap: 2px;
    height: 10px;
    overflow: hidden;
    border-radius: var(--ebc-radius-pill);
}

.compose-bar span {
    flex-basis: 0;
    min-width: 3px;
}

.compose-list {
    display: grid;
    grid-template-columns: 8px minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 1px 6px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 11px;
}

.compose-list li {
    display: contents;
}

.compose-list i {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 2px;
}

.compose-name {
    color: var(--kpi-text-sub);
    white-space: nowrap;
}

.compose-share {
    padding-left: 8px;
    color: var(--kpi-text-muted);
    text-align: right;
}

.compose-list b {
    padding-left: 10px;
    font-weight: 700;
    text-align: right;
    color: var(--kpi-text);
    white-space: nowrap;
}

.compose-empty {
    font-size: 12px;
    color: var(--kpi-text-muted);
}

.kpi-info {
    display: grid;
    gap: 6px 16px;
    margin: 0;
    align-content: center;
}

.kpi-info.is-wide {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

.kpi-info.is-narrow {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.kpi-info > div {
    min-width: 0;
}

.kpi-info > div.is-span {
    grid-column: span 2;
}

.kpi-info__tag {
    margin-left: 6px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 700;
    color: var(--kpi-warn);
    border-radius: var(--ebc-radius-pill);
    background: var(--kpi-chip);
}

.kpi-info dt {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    color: var(--kpi-text-sub);
}

.kpi-info__icon {
    font-size: 11px;
    color: var(--ebc-gray-4);
    cursor: help;
}

.kpi-info__icon:hover {
    color: #ffffff;
}

.kpi-info dd {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--kpi-text);
}

.kpi-info__value {
    font-size: 15px;
    font-weight: 800;
}

.kpi-info__value.is-text {
    font-size: 13px;
    font-weight: 700;
}

.kpi-info dd small {
    margin-left: 2px;
    font-size: 11px;
    font-weight: 600;
    color: var(--kpi-text-sub);
}

.kpi-info__sub {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
